import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useProducer } from '../../common/hooks/useProducer';
import { ordersAPI, productsAPI, pricesAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Input from '../../common/components/Input';
import Card from '../../common/components/Card';
import Loading from '../../common/components/Loading';
import { ArrowLeft, Plus, Trash2, Package } from 'lucide-react';
import { formatCurrency } from '../../common/utils/formatters';
import toast from 'react-hot-toast';
import './AddOrderScreen.css';

const AddOrderScreen = () => {
  const navigate = useNavigate();
  const { profile } = useProducer();
  const queryClient = useQueryClient();

  // Form state
  const [orderData, setOrderData] = useState({
    customerName: '',
    customerAddress: '',
    orderSource: 'Manual',
  });

  const [orderItems, setOrderItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Fetch products and prices
  const { data: products, isLoading: loadingProducts } = useQuery('products', async () => {
    const response = await productsAPI.list();
    return response.data.data;
  });

  const { data: allPrices, isLoading: loadingPrices } = useQuery('prices', async () => {
    const response = await pricesAPI.list();
    return response.data.data;
  });

  // Get prices for selected product
  const productPrices = selectedProduct
    ? allPrices?.filter(p => p.product_id === parseInt(selectedProduct)) || []
    : [];

  // Create order mutation
  const createOrderMutation = useMutation(
    async (data) => {
      const response = await ordersAPI.create(data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
        toast.success('Order created successfully');
        navigate(`/${profile.url_friendly_name}/orders`);
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to create order');
      },
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddItem = () => {
    if (!selectedPrice || !quantity) {
      toast.error('Please select a product/size and enter quantity');
      return;
    }

    const priceInfo = allPrices.find(p => p.id === parseInt(selectedPrice));
    if (!priceInfo) {
      toast.error('Invalid price selection');
      return;
    }

    const product = products.find(p => p.id === priceInfo.product_id);
    if (!product) {
      toast.error('Product not found');
      return;
    }

    // Check if item already exists
    const existingItemIndex = orderItems.findIndex(
      item => item.productSizePriceId === priceInfo.id
    );

    if (existingItemIndex >= 0) {
      // Update quantity
      const updatedItems = [...orderItems];
      updatedItems[existingItemIndex].quantity += parseInt(quantity);
      setOrderItems(updatedItems);
    } else {
      // Add new item
      const newItem = {
        productSizePriceId: priceInfo.id,
        productName: product.name,
        sizeName: priceInfo.size_option_name,
        quantity: parseInt(quantity),
        unitPrice: parseFloat(priceInfo.price),
      };
      setOrderItems([...orderItems, newItem]);
    }

    // Reset selection
    setSelectedProduct('');
    setSelectedPrice('');
    setQuantity(1);
  };

  const handleRemoveItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!orderData.customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }

    if (!orderData.customerAddress.trim()) {
      toast.error('Please enter customer address');
      return;
    }

    if (orderItems.length === 0) {
      toast.error('Please add at least one item to the order');
      return;
    }

    const orderPayload = {
      ...orderData,
      items: orderItems,
    };

    createOrderMutation.mutate(orderPayload);
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  };

  if (loadingProducts || loadingPrices) {
    return <Loading message="Loading products..." />;
  }

  const productsList = products || [];

  return (
    <div className="add-order-screen">
      <div className="screen-header">
        <Button
          variant="secondary"
          icon={<ArrowLeft size={20} />}
          onClick={() => navigate(`/${profile.url_friendly_name}/orders`)}
        >
          Back
        </Button>
        <h1>
          <Package size={32} />
          Add New Order
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="order-form-layout">
          <div className="order-details-section">
            <Card>
              <h2>Order Details</h2>
              
              <Input
                label="Customer Name"
                name="customerName"
                value={orderData.customerName}
                onChange={handleInputChange}
                placeholder="Enter customer name"
                required
              />

              <div className="form-group">
                <label>Customer Address</label>
                <textarea
                  name="customerAddress"
                  value={orderData.customerAddress}
                  onChange={handleInputChange}
                  placeholder="Enter delivery address"
                  rows="3"
                  required
                  className="form-textarea"
                />
              </div>

              <div className="form-group">
                <label>Order Source</label>
                <select
                  name="orderSource"
                  value={orderData.orderSource}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="Manual">Manual</option>
                  <option value="FarmFirst">FarmFirst</option>
                </select>
              </div>
            </Card>

            <Card>
              <h2>Add Items</h2>
              
              <div className="form-group">
                <label>Product</label>
                <select
                  value={selectedProduct}
                  onChange={(e) => {
                    setSelectedProduct(e.target.value);
                    setSelectedPrice('');
                  }}
                  className="form-select"
                >
                  <option value="">Select a product</option>
                  {productsList.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.category_name || 'Uncategorized'})
                    </option>
                  ))}
                </select>
              </div>

              {selectedProduct && (
                <>
                  <div className="form-group">
                    <label>Size / Price</label>
                    <select
                      value={selectedPrice}
                      onChange={(e) => setSelectedPrice(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select size</option>
                      {productPrices.map(price => (
                        <option key={price.id} value={price.id}>
                          {price.size_option_name} - {formatCurrency(price.price)}
                          {price.stock_quantity > 0 ? ` (Stock: ${price.stock_quantity})` : ' (Out of stock)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      min="1"
                      className="form-input"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    icon={<Plus size={20} />}
                    onClick={handleAddItem}
                    disabled={!selectedPrice}
                  >
                    Add to Order
                  </Button>
                </>
              )}
            </Card>
          </div>

          <div className="order-items-section">
            <Card>
              <h2>Order Items</h2>
              
              {orderItems.length === 0 ? (
                <div className="no-items">
                  <p>No items added yet</p>
                </div>
              ) : (
                <>
                  <div className="order-items-list">
                    {orderItems.map((item, index) => (
                      <div key={index} className="order-item">
                        <div className="item-info">
                          <div className="item-name">{item.productName}</div>
                          <div className="item-size">{item.sizeName}</div>
                          <div className="item-quantity">Qty: {item.quantity}</div>
                        </div>
                        <div className="item-price">
                          {formatCurrency(item.quantity * item.unitPrice)}
                        </div>
                        <button
                          type="button"
                          className="item-remove"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="order-total">
                    <span>Total:</span>
                    <span className="total-amount">{formatCurrency(calculateTotal())}</span>
                  </div>
                </>
              )}
            </Card>

            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(`/${profile.url_friendly_name}/orders`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={createOrderMutation.isLoading || orderItems.length === 0}
              >
                {createOrderMutation.isLoading ? 'Creating...' : 'Create Order'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddOrderScreen;
